import { useState } from 'react'
import { Link } from 'react-router'
import { getActiveTest } from '#app/modules/test-management/index.ts'
import { SITE_NAME, ASSESSMENT_NAME } from '#app/utils/branding.ts'
import { cn } from '#app/utils/misc.tsx'
import { type Route } from './+types/support.ts'

const CONTACT_EMAIL = 'support@ikonexamen.com'

export const meta: Route.MetaFunction = () => [
	{ title: `Support - ${SITE_NAME}` },
	{
		name: 'description',
		content: `Get help with ${SITE_NAME} — contact support, read frequently asked questions, and find answers.`,
	},
]

function getFaqs(
	totalQuestions: number,
): { question: string; answer: string }[] {
	return [
		{
			question: `How long does ${ASSESSMENT_NAME} take?`,
			answer: `Most people complete the ${totalQuestions} questions in 10–20 minutes. Your progress is automatically saved, so you can close the page and return later without losing your answers.`,
		},
		{
			question: 'Can I retake the assessment?',
			answer:
				'Yes. Each purchase includes a set number of assessment attempts depending on your tier. You can start a new attempt from your dashboard whenever you have remaining attempts available.',
		},
		{
			question: 'What does the AI-generated narrative include?',
			answer: `After you complete all ${totalQuestions} questions, your responses are scored across four sections — Orientation, Temperament, Potential, and Character. An AI model then generates a personalized narrative portrait that integrates these scores into a cohesive interpretation of who you are, what you carry, and how you were formed.`,
		},
		{
			question: 'How is my data used and protected?',
			answer:
				'Your assessment responses, scores, and narrative are stored securely and associated with your account. We do not sell your data or share it with third parties beyond what is necessary to operate the service (infrastructure, payment processing, AI narrative generation). See our Privacy Policy for full details.',
		},
		{
			question: 'Can I download my results?',
			answer:
				'Yes. After completing the assessment, you can download a PDF of your full results — including dimension scores and narrative — directly from the results page. You can also have the results emailed to your account address.',
		},
		{
			question: 'What payment methods do you accept?',
			answer:
				'We use Stripe for secure payment processing. Stripe supports all major credit and debit cards, Apple Pay, Google Pay, and other local payment methods depending on your region. We never store your card details.',
		},
		{
			question: "I completed payment but don't see my purchase.",
			answer:
				"Payment processing typically takes a few seconds. If your purchase doesn't appear on your dashboard within a minute, try refreshing the page. If the issue persists, contact us at support@ikonexamen.com with your account email and we'll resolve it promptly.",
		},
		{
			question: 'Can I get a refund?',
			answer:
				"Purchases are generally non-refundable once assessment access has been granted. If you experience a technical issue that prevents you from completing the assessment, please contact support and we'll work with you to find a resolution.",
		},
	]
}

export async function loader() {
	const activeTest = await getActiveTest()
	const totalQuestions = activeTest.sections.reduce(
		(sum, section) => sum + section.questions.length,
		0,
	)
	return { totalQuestions }
}

export default function SupportRoute({ loaderData }: Route.ComponentProps) {
	const { totalQuestions } = loaderData
	const faqs = getFaqs(totalQuestions)

	return (
		<main className="bg-cathedral-page min-h-screen px-6 py-16 md:py-24">
			<div className="mx-auto w-full max-w-4xl">
				{/* Navigation */}
				<div className="mb-12 flex items-center justify-between gap-4">
					<Link
						to="/"
						className="cathedral-heading text-cathedral-brown hover:text-cathedral-gold text-base tracking-[0.12em] uppercase transition-colors"
					>
						{SITE_NAME}
					</Link>
					<div className="flex items-center gap-3">
						<Link
							to="/login"
							className="cathedral-heading text-cathedral-gold hover:text-cathedral-gold-light border-cathedral-gold/30 rounded-sm border px-4 py-2 text-sm tracking-[0.14em] uppercase transition-colors"
						>
							Log In
						</Link>
					</div>
				</div>

				{/* Hero */}
				<header className="mx-auto max-w-3xl text-center">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.16em] uppercase">
						Help & Support
					</p>
					<h1 className="cathedral-heading text-cathedral-dark mt-4 text-5xl font-semibold tracking-wide md:text-6xl">
						How Can We Help?
					</h1>
					<p className="cathedral-body text-cathedral-brown mt-5 text-xl leading-relaxed md:text-2xl md:leading-relaxed">
						Find answers to common questions or reach out to our support team
						directly.
					</p>
				</header>

				{/* Divider */}
				<div
					className="mx-auto my-14 flex w-full max-w-xl items-center gap-4 md:my-16"
					aria-hidden
				>
					<div className="via-gold/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="via-gold/30 h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
				</div>

				{/* Contact */}
				<section className="bg-cathedral-card mx-auto max-w-3xl rounded-xl border p-8 text-center md:p-10">
					<h2 className="cathedral-heading text-cathedral-dark text-2xl font-semibold tracking-wide md:text-3xl">
						Contact Support
					</h2>
					<p className="cathedral-body text-cathedral-brown mt-4 text-lg leading-relaxed">
						If you need help with your account, a purchase, or your assessment
						results, reach out and we'll respond as quickly as we can.
					</p>
					<a
						href={`mailto:${CONTACT_EMAIL}`}
						className="cathedral-heading border-cathedral-gold bg-cathedral-bright text-cathedral-deep mt-6 inline-block rounded-sm border-2 px-8 py-3 text-sm tracking-[0.15em] uppercase transition-opacity hover:opacity-90"
					>
						{CONTACT_EMAIL}
					</a>
				</section>

				{/* Divider */}
				<div
					className="mx-auto my-14 flex w-full max-w-xl items-center gap-4 md:my-16"
					aria-hidden
				>
					<div className="via-gold/30 h-px flex-1 bg-gradient-to-r from-transparent to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="via-gold/30 h-px flex-1 bg-gradient-to-r from-transparent to-transparent" />
				</div>

				{/* FAQ */}
				<section className="mx-auto max-w-3xl">
					<h2 className="cathedral-heading text-cathedral-dark text-center text-3xl font-semibold tracking-wide md:text-4xl">
						Frequently Asked Questions
					</h2>

					<div className="mt-10 space-y-3">
						{faqs.map((faq, index) => (
							<FaqItem
								key={index}
								question={faq.question}
								answer={faq.answer}
							/>
						))}
					</div>
				</section>

				{/* Footer */}
				<footer className="border-cathedral-gold/20 mt-16 border-t pt-8">
					<div className="cathedral-body text-cathedral-brown flex flex-wrap items-center justify-center gap-6 text-sm md:text-base">
						<Link
							to="/privacy"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Privacy Policy
						</Link>
						<Link
							to="/tos"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Terms of Service
						</Link>
						<Link
							to="/about"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							About
						</Link>
						<Link
							to="/"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Home
						</Link>
					</div>
				</footer>
			</div>
		</main>
	)
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<div className="bg-cathedral-card rounded-lg border">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
				aria-expanded={isOpen}
			>
				<span className="cathedral-heading text-cathedral-dark text-base leading-snug font-semibold md:text-lg">
					{question}
				</span>
				<span
					className={cn(
						'text-cathedral-gold shrink-0 text-xl transition-transform duration-200',
						isOpen && 'rotate-45',
					)}
					aria-hidden
				>
					+
				</span>
			</button>
			<div
				className={cn(
					'grid transition-all duration-200 ease-out',
					isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
				)}
			>
				<div className="overflow-hidden">
					<p className="cathedral-body text-cathedral-brown px-6 pb-5 text-base leading-relaxed md:text-lg">
						{answer}
					</p>
				</div>
			</div>
		</div>
	)
}
